import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { withRouter } from 'react-router'
import ReactGA from "react-ga4"
import getWidgetsOrigin from '../getWidgetsOrigin'
import getStrings from 'getStrings'
import { updateIntegrationsWithNoResponse } from "../../Uluweb"
import { registerPlayfabUser, executeAddOrUpdateUser, linkCustomID } from "../../Playfab"
import getLanguage from 'getLanguage'
import getUrlParameters from 'getUrlParameters'
import AppContext from 'core/appContext'

const ERRORS = { // This contains errors coming both from Uluweb and Playfab, hence the different format.
    en: {
        GENERIC_ERROR: 'Our servers encountered an error.',
        INVALID_PAYLOAD: 'Please verify that your email and password are valid.',
        USER_ALREADY_EXISTS: 'This user already exists.',
        EmailAddressNotAvailable: 'This email address is not available. Have you already created an account?',
        InvalidEmailAddress: 'This email address is not valid',
        InvalidEmailOrPassword: 'This email address or password is not valid',
        InvalidPassword: 'This password is not valid'
    },
    fr: {
        GENERIC_ERROR: 'Nos serveurs ont rencontré une erreur.',
        INVALID_PAYLOAD: 'Veuillez vérifier que votre courriel et votre mot de passe sont valides.',
        USER_ALREADY_EXISTS: 'Cet utilisateur existe déjà.',
        EmailAddressNotAvailable: "Cette adresse courriel n'est pas disponible. Avez-vous déjà créé un compte?",
        InvalidEmailAddress: "Cette adresse courriel n'est pas valide",
        InvalidEmailOrPassword: "Cette adresse courriel ou ce mot de passe n'est pas valide",
        InvalidPassword: "Ce mot de passe n'est pas valide"
    }
}

class WidgetsSignUpForm extends React.Component {

    constructor(props) {
        super(props)

        const origin = getWidgetsOrigin()
        console.log('widget origin: ', origin)

        let parentOrigin
        if (origin === 'web2wave')
            parentOrigin = 'https://ululab.web2wave.com'
        else if (origin === 'funnelululabcom')
            parentOrigin = 'https://funnel.ululab.com'
        else if (origin === 'quizululabcom')
            parentOrigin = 'https://quiz.ululab.com'
        else if (origin === 'quizdevululabcom')
            parentOrigin = 'https://quizdev.ululab.com'
        else if (origin === 'quizgabrielululabcom')
            parentOrigin = 'https://quizgabriel.ululab.com'
        else
            parentOrigin = origin

        this.state = {
            parentOrigin: parentOrigin
        }
    }

    componentDidMount() {
        // eslint-disable-next-line no-undef
        gtag('get', process.env['REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID'], 'client_id', (clientID) => {
            this.setState({googleClientID: clientID})
        })
    }

    createAccountClicked = async (event) => {
        event.preventDefault()

        const strings = getStrings()

        if (!document.forms[event.target.dataset.form].reportValidity()) return        // EXIT

        const callerFormName = event.target.dataset.form
        const formData = this.getFormDataJSON(callerFormName)

        if (!this.state.parentOrigin) {
            alert('Invalid parent origin!')
            return
        }

        const email = formData.Email

        if (!email || !formData.Password) {
            alert(strings.PARENT_PORTAL_SIGN_UP_FORM_ERROR_REQUIRED_FIELDS)
            return  // EXIT
        }

        this.blockUI(true)

        window.fbq('trackCustom', 'StartRegisteringCustom', {
            content_name: 'Starting The Sign Up process',
            status: 'Success'
        })

        const uluwebToken = uuidv4()

        const playfabAPIResponse = await this.tryRegisterPlayfabUser(email, formData.Password, uluwebToken)
        if (!playfabAPIResponse) // Playfab returned an error
            return  // EXIT

        const playfabID = playfabAPIResponse.data.data.PlayFabId
        const entityToken = playfabAPIResponse.data.data.EntityToken.EntityToken
        const createUserResponse = await this.tryCreateUser(entityToken, uluwebToken, playfabID)

        if (createUserResponse && createUserResponse.success) {
            ReactGA.event('sign_up', createUserResponse)

            const userID = createUserResponse.id

            await updateIntegrationsWithNoResponse(uluwebToken)

            // Keep session data for access to the account managing dashboard.
            const sessionObject = {
                ...playfabAPIResponse.data,
                token: uluwebToken,
                email: email
            }

            // create the deep links
            const oneLinkQueryParams = `?playfab_token=${uluwebToken}`
            const appStore = `${process.env['REACT_APP_ONELINK_IOS']}/${oneLinkQueryParams}`
            const googlePlay = `${process.env['REACT_APP_ONELINK_ANDROID']}/${oneLinkQueryParams}`

            console.log('sending session message. session: ', JSON.stringify(sessionObject))
            console.log('token: ', uluwebToken)
            console.log('userID: ', userID)
            console.log('playfabID: ', playfabID)
            console.log('appStore: ', appStore)
            console.log('googlePlay: ', googlePlay)
            window.parent.postMessage(
                {
                    type: "AUTH_SUCCESS",
                    session: JSON.stringify(sessionObject),
                    userID: userID,
                    playfabID: playfabID,
                    email: email,
                    token: uluwebToken,
                    appStore: appStore,
                    googlePlay: googlePlay
                },
                this.state.parentOrigin
            )
        }

        this.blockUI(false)
    }

    tryRegisterPlayfabUser = async (email, password, uuid) => {
        let playfabAPIResponse = null

        try {
            playfabAPIResponse = await registerPlayfabUser(email, password)
        } catch (err) {
            this.blockUI(false)
            this.displayError(err.response.data.error)
            return
        }

        try {
            await this.tryLinkCustomIDs(playfabAPIResponse.data.data, uuid)
        } catch (err) {
            console.error(err) // Don't prevent execution for an error here as this only allows auto-login
        }

        return playfabAPIResponse
    }

    tryLinkCustomIDs = async (playfabAPIResponseData, uuid) => {
        // If those fail, the user won't be able to auto-login
        try {
            await linkCustomID(playfabAPIResponseData.SessionTicket, uuid)
        } catch (err) {
            // In an ideal world, we'd have a queue system the wouldn't block execution and would handle retries
            console.error(err)
        }
    }

    getFormDataJSON = (formName) => Object.fromEntries(new FormData(document.forms[formName]).entries())

    tryCreateUser = async (entityToken, uluwebToken, playfabID) => {
        try {
            const createUserResponse = await executeAddOrUpdateUser(entityToken, uluwebToken, playfabID, true)

            if (!createUserResponse.data.data.FunctionResult.success)
                this.displayError(createUserResponse.data.data.FunctionResult.error)
            else if (!createUserResponse.data.data.FunctionResult.userAPIResponse.success)
                this.displayError(createUserResponse.data.data.FunctionResult.userAPIResponse.error)
            else
                return createUserResponse.data.data.FunctionResult.userAPIResponse
        } catch (err) {
            console.error(err)
            this.displayError(err)
        }
    }

    blockUI = (shouldBlock) => this.context.setBlockUI(shouldBlock)

    displayError = (errorCode) => {
        const language = getLanguage()
        if (!errorCode || Object.keys(errorCode).length === 0) {
            console.error('signup.displayError: generic: ', errorCode)
            return alert(ERRORS[language].GENERIC_ERROR)
        }

        console.error(`signup.displayError: ${errorCode}`)
        alert(ERRORS[language].hasOwnProperty(errorCode) ? ERRORS[language][errorCode] : ERRORS[language]['GENERIC_ERROR'])
    }

    render() {
        const strings = getStrings()

        return (
            <div className='form-box'>
                <p><strong>{strings.PARENT_PORTAL_SIGN_UP_FORM_HAVE_ACCOUNT_P}{' '}
                    <a href={`/signin?${getUrlParameters()}`} target={'_blank'}>{strings.PARENT_PORTAL_SIGN_UP_FORM_HAVE_ACCOUNT_LINK}</a>
                </strong></p>
                <form id='formCreateAccount'>
                    <input name='Email' type={'email'} placeholder={strings.PARENT_PORTAL_FORM_EMAIL} required className='margin-bottom-2' />
                    <input name='Password' type={'password'} placeholder={strings.PARENT_PORTAL_FORM_PASSWORD} minLength={8} required className='margin-bottom-2' />
                    <button
                        data-form='formCreateAccount'
                        onClick={this.createAccountClicked.bind(this)}
                        className="form-button"
                        type='submit'
                        style={{ backgroundColor: '#FF9408' }}
                    >
                        {strings.PARENT_PORTAL_FORM_BUTTON}
                    </button>
                </form>
            </div>
        )
    }
}

WidgetsSignUpForm.contextType = AppContext

export default withRouter(WidgetsSignUpForm)